import React, { PureComponent as Component } from 'react';
import propTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Progress,
  Badge
} from 'reactstrap';
import { loadPresets, savePresets } from '../../utils/exportClient';

export const DELIVERY_DOWNLOAD = 'download';
export const DELIVERY_EMAIL = 'email';

const sectionStyle = { fontSize: '.66rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase' };
const labelStyle = { fontWeight: 600, fontSize: '.78rem' };

// ExportModal is the one export dialog for every dapur list screen: the
// screen supplies its filter block and column catalogue; the modal owns the
// columns picker, presets, the live row count and the two delivery modes.
export default class ExportModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: { ...props.defaultFilters },
      columns: this._defaultColumns(props),
      delivery: DELIVERY_DOWNLOAD,
      total: null,
      counting: false,
      countStale: false,
      presets: loadPresets(props.screen),
      presetName: ''
    };
    this._setFilter = this._setFilter.bind(this);
    this._toggleColumn = this._toggleColumn.bind(this);
    this._toggleAll = this._toggleAll.bind(this);
    this._clear = this._clear.bind(this);
    this._savePreset = this._savePreset.bind(this);
    this._applyPreset = this._applyPreset.bind(this);
    this._deletePreset = this._deletePreset.bind(this);
    this._run = this._run.bind(this);
    this._recount = this._recount.bind(this);
    this._unmounted = false;
  }

  componentWillUnmount() {
    this._unmounted = true;
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this._recount();
    }
  }

  _visibleColumns(props = this.props) {
    return props.columns.filter(c => !c.adminOnly || props.isAdmin);
  }

  _availableColumns(props = this.props, delivery = this.state ? this.state.delivery : DELIVERY_DOWNLOAD) {
    return this._visibleColumns(props).filter(c => !c.emailOnly || delivery === DELIVERY_EMAIL);
  }

  _defaultColumns(props) {
    return this._visibleColumns(props).map(c => c.name);
  }

  // One COUNT when the modal opens, then only when the admin asks; a filter
  // change marks the number stale instead of querying again.
  _recount() {
    if (!this.props.countRows) return;
    this.setState({ counting: true, countStale: false });
    this.props.countRows(this.state.filters)
      .then(total => { if (!this._unmounted) this.setState({ total, counting: false }); })
      .catch(() => { if (!this._unmounted) this.setState({ total: null, counting: false, countStale: true }); });
  }

  _setFilter(name, value) {
    this.setState({ filters: { ...this.state.filters, [name]: value }, countStale: true });
  }

  _toggleColumn(name) {
    const { columns } = this.state;
    this.setState({ columns: columns.includes(name) ? columns.filter(c => c !== name) : [...columns, name] });
  }

  _toggleAll(e) {
    this.setState({ columns: e.target.checked ? this._availableColumns().map(c => c.name) : [] });
  }

  _clear() {
    this.setState({ filters: { ...this.props.defaultFilters }, columns: this._defaultColumns(this.props), presetName: '', countStale: true });
  }

  _savePreset() {
    const name = this.state.presetName.trim();
    if (!name) return;
    const presets = this.state.presets.filter(p => p.name !== name)
      .concat([{ name, filters: this.state.filters, columns: this.state.columns, delivery: this.state.delivery }]);
    savePresets(this.props.screen, presets);
    this.setState({ presets, presetName: '' });
  }

  _applyPreset(e) {
    const preset = this.state.presets.find(p => p.name === e.target.value);
    if (!preset) return;
    const allowed = this._defaultColumns(this.props);
    this.setState({
      filters: { ...this.props.defaultFilters, ...preset.filters },
      columns: preset.columns.filter(c => allowed.includes(c)),
      delivery: preset.delivery || DELIVERY_DOWNLOAD,
      presetName: preset.name,
      countStale: true
    });
  }

  _deletePreset() {
    const presets = this.state.presets.filter(p => p.name !== this.state.presetName);
    savePresets(this.props.screen, presets);
    this.setState({ presets, presetName: '' });
  }

  _orderedColumns() {
    const chosen = new Set(this.state.columns);
    return this._availableColumns().map(c => c.name).filter(n => chosen.has(n));
  }

  _run() {
    const columns = this._orderedColumns();
    if (this.state.delivery === DELIVERY_EMAIL) {
      this.props.onEmail(this.state.filters, columns);
    } else {
      this.props.onDownload(this.state.filters, columns);
    }
  }

  render() {
    const { isOpen, toggle, title, isExporting, progress, rowCount, maxRows, isAdmin, emailStatus, userEmail } = this.props;
    const { filters, columns, delivery, total, counting, countStale, presets, presetName } = this.state;
    const available = this._availableColumns();
    const allOn = available.every(c => columns.includes(c.name));
    const chosenCount = available.filter(c => columns.includes(c.name)).length;
    const caveat = this.props.countCaveat ? this.props.countCaveat(filters, delivery) : '';
    const overCap = delivery === DELIVERY_DOWNLOAD && !countStale && total !== null && total > maxRows;
    const busy = isExporting;
    const totalText = counting ? '…' : (total === null ? '?' : total.toLocaleString());
    return (
      <Modal isOpen={isOpen} toggle={busy ? undefined : toggle} size="lg">
        <ModalHeader toggle={busy ? undefined : toggle}>
          <i className="fa fa-download text-muted"></i> {title}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3" style={{ fontSize: '.75rem' }}>
            Starts from the filter panel. Everything below narrows it further; blank means all.
          </p>
          <Form onSubmit={e => e.preventDefault()}>
            {presets.length > 0 && (
              <FormGroup className="d-flex align-items-center" style={{ gap: '8px' }}>
                <Label className="mb-0" style={labelStyle}>Preset</Label>
                <Input bsSize="sm" type="select" value={presetName} onChange={this._applyPreset} disabled={busy} style={{ maxWidth: '260px' }}>
                  <option value="">None</option>
                  {presets.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </Input>
                {presetName && presets.some(p => p.name === presetName) && (
                  <Button size="sm" color="link" className="text-danger p-0" onClick={this._deletePreset} disabled={busy}>delete</Button>
                )}
              </FormGroup>
            )}

            {this.props.renderFilters(filters, this._setFilter, busy)}

            <div className="d-flex align-items-center mt-3 mb-2" style={{ gap: '12px' }}>
              <span className="text-muted" style={sectionStyle}>Columns</span>
              <label className="mb-0 d-flex align-items-center" style={{ gap: '6px', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" style={{ position: 'static', margin: 0 }} checked={allOn} onChange={this._toggleAll} disabled={busy} /> All
              </label>
              <span className="text-muted" style={{ fontSize: '.72rem' }}>{chosenCount} of {available.length} selected</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '2px 12px' }}>
              {this.props.columns.map(c => {
                const locked = c.adminOnly && !isAdmin;
                const emailOnlyOff = c.emailOnly && delivery !== DELIVERY_EMAIL;
                const off = locked || emailOnlyOff;
                return (
                  <label key={c.name} className={`mb-0 d-flex align-items-center${off ? ' text-muted' : ''}`} style={{ gap: '6px', fontSize: '.76rem', minWidth: 0, cursor: off ? 'default' : 'pointer' }} title={c.name}>
                    <input type="checkbox" style={{ position: 'static', margin: 0, flexShrink: 0 }} checked={!off && columns.includes(c.name)}
                      onChange={() => this._toggleColumn(c.name)} disabled={busy || off || c.name === 'id'} />
                    <span data-col={c.name} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                    {c.adminOnly && <Badge color="light" className="border" style={{ fontSize: '.6rem', flexShrink: 0 }}><i className="fa fa-lock"></i> admin</Badge>}
                    {c.emailOnly && <Badge color="light" className="border" style={{ fontSize: '.6rem', flexShrink: 0 }}><i className="fa fa-envelope-o"></i> email</Badge>}
                  </label>
                );
              })}
            </div>

            <div className="text-muted mt-3 mb-2" style={sectionStyle}>Delivery</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                [DELIVERY_DOWNLOAD, 'fa-download', 'Download now', `Up to ${maxRows.toLocaleString()} rows, fetched page by page from the backoffice API.`],
                [DELIVERY_EMAIL, 'fa-envelope-o', 'Send by email', `Complete list, any size. Built on the server in paced chunks, link emailed to ${userEmail || 'you'}.`]
              ].map(([key, icon, name, desc]) => (
                <div key={key} onClick={() => !busy && this.setState({ delivery: key })}
                  style={{ border: `1px solid ${delivery === key ? '#20a8d8' : '#c2cfd6'}`, boxShadow: delivery === key ? 'inset 0 0 0 1px #20a8d8' : 'none', borderRadius: '4px', padding: '10px 12px', cursor: busy ? 'default' : 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: '.8rem' }}><i className={`fa ${icon} text-muted mr-2`}></i>{name}</div>
                  <div className="text-muted" style={{ fontSize: '.72rem', marginTop: '3px' }}>{desc}</div>
                </div>
              ))}
            </div>
            <div className="d-flex align-items-center justify-content-between mt-2 px-2 py-2" style={{ background: '#f0f3f5', border: '1px solid #e4e7ea', borderRadius: '3px' }}>
              <span style={{ fontSize: '.78rem' }}>
                Matching rows after these filters{caveat && <span className="text-muted"> ({caveat})</span>}
                {countStale && !counting && (
                  <button type="button" className="btn btn-link btn-sm p-0 ml-2 align-baseline" style={{ fontSize: '.75rem' }} onClick={this._recount} disabled={busy}>filters changed, count again</button>
                )}
              </span>
              <b style={{ fontSize: '.95rem', opacity: countStale ? 0.45 : 1 }}>{totalText}</b>
            </div>
            {overCap && (
              <p className="text-danger mb-0 mt-2" style={{ fontSize: '.75rem' }}>
                {total.toLocaleString()} rows exceed the {maxRows.toLocaleString()} row download limit. Narrow the filter, or switch to Send by email.
              </p>
            )}
            {emailStatus && (
              <p className="mb-0 mt-2" style={{ fontSize: '.75rem' }}>
                <i className="fa fa-envelope-o text-muted mr-1"></i>
                Email export <b>{emailStatus.state}</b>{emailStatus.rows ? `, ${emailStatus.rows.toLocaleString()} rows so far` : ''}{emailStatus.message ? `: ${emailStatus.message}` : ''}
                {emailStatus.dropped_columns && emailStatus.dropped_columns.length > 0 && ` (not included: ${emailStatus.dropped_columns.join(', ')})`}
              </p>
            )}
          </Form>
        </ModalBody>
        <ModalFooter className="d-block">
          {isExporting && delivery === DELIVERY_DOWNLOAD && (
            <div className="d-flex align-items-center mb-2" style={{ gap: '10px' }}>
              <Progress value={progress} style={{ height: '6px', flex: 1 }} />
              <span className="text-muted" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>fetched {rowCount} rows… {progress}%</span>
            </div>
          )}
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <Button size="sm" color="light" className="border" onClick={this._clear} disabled={busy}>Clear</Button>
            <Input bsSize="sm" placeholder="Preset name" value={presetName} onChange={e => this.setState({ presetName: e.target.value })} disabled={busy} style={{ maxWidth: '180px' }} />
            <Button size="sm" color="light" className="border" onClick={this._savePreset} disabled={busy || !presetName.trim()}>Save as preset</Button>
            <span className="ml-auto"></span>
            <Button size="sm" color="secondary" onClick={this._run} disabled={busy || overCap || chosenCount === 0}>
              <i className={busy ? 'fa fa-spinner fa-spin' : (delivery === DELIVERY_EMAIL ? 'fa fa-envelope-o' : 'fa fa-download')}></i>
              &nbsp; {busy ? 'Working…' : (delivery === DELIVERY_EMAIL ? 'Send by email' : 'Download CSV')}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

ExportModal.propTypes = {
  isOpen: propTypes.bool,
  toggle: propTypes.func,
  title: propTypes.string.isRequired,
  screen: propTypes.string.isRequired,
  columns: propTypes.arrayOf(propTypes.shape({ name: propTypes.string.isRequired, adminOnly: propTypes.bool, emailOnly: propTypes.bool })).isRequired,
  isAdmin: propTypes.bool,
  defaultFilters: propTypes.object,
  renderFilters: propTypes.func.isRequired,
  countRows: propTypes.func,
  countCaveat: propTypes.func,
  onDownload: propTypes.func.isRequired,
  onEmail: propTypes.func.isRequired,
  isExporting: propTypes.bool,
  progress: propTypes.number,
  rowCount: propTypes.number,
  maxRows: propTypes.number,
  emailStatus: propTypes.object,
  userEmail: propTypes.string
};

ExportModal.defaultProps = {
  isOpen: false,
  isAdmin: false,
  defaultFilters: {},
  isExporting: false,
  progress: 0,
  rowCount: 0,
  maxRows: 20000,
  emailStatus: null,
  userEmail: ''
};
